import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import type { ModelAuth, ModelConfig } from '../types';
import { reportError } from '../utils/errors';
import './UiValidationSettingsPage.lane37.css';

type UiValidationSelectedModel = {
  id: string;
  name: string;
  provider: string;
  model?: string | null;
  is_global: boolean;
  auth_id?: string | null;
  auth_name?: string | null;
  auth_type?: string | null;
  auth_is_global?: boolean | null;
};

type UiValidationSetupStatus = {
  ready: boolean;
  model_configured: boolean;
  model_exists: boolean;
  model_is_global: boolean;
  auth_configured: boolean;
  auth_is_global: boolean;
  message: string;
};

type UiValidationSettings = {
  enabled: boolean;
  model_id?: string | null;
  model_access_set_id?: string | null;
  runtime: string;
  codex_home_profile: string;
  selected_model?: UiValidationSelectedModel | null;
  setup_status: UiValidationSetupStatus;
};

export default function UiValidationSettingsPage({
  apiClient,
  setError
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
}) {
  const [settings, setSettings] = useState<UiValidationSettings | null>(null);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [auths, setAuths] = useState<ModelAuth[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [modelId, setModelId] = useState('');
  const [modelAccessSetId, setModelAccessSetId] = useState('');
  const [runtime, setRuntime] = useState('dind-docker');
  const [codexHomeProfile, setCodexHomeProfile] = useState('validation');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      apiClient.get<UiValidationSettings>('/api/settings/ui-validation'),
      apiClient.get<ModelConfig[]>('/api/me/models'),
      apiClient.get<ModelAuth[]>('/api/me/model-auths')
    ])
      .then(([settingsData, modelData, authData]) => {
        setSettings(settingsData);
        setModels(Array.isArray(modelData) ? modelData : []);
        setAuths(Array.isArray(authData) ? authData : []);
        setEnabled(settingsData.enabled);
        setModelId(settingsData.model_id ?? '');
        setModelAccessSetId(settingsData.model_access_set_id ?? '');
        setRuntime(settingsData.runtime || 'dind-docker');
        setCodexHomeProfile(settingsData.codex_home_profile || 'validation');
        setLocalError(null);
      })
      .catch((err) => reportError('Failed to load UI validation settings', err, setLocalError))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient]);

  const codexModels = useMemo(
    () => models.filter((model) => model.provider === 'codex' && model.is_global),
    [models]
  );

  const selectedModel = useMemo(
    () => models.find((model) => model.id === modelId) ?? null,
    [modelId, models]
  );

  const selectedAuth = useMemo(
    () => auths.find((auth) => auth.id === selectedModel?.auth_id) ?? null,
    [auths, selectedModel]
  );

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSuccess(null);
    try {
      const updated = await apiClient.post<UiValidationSettings>('/api/settings/ui-validation', {
        enabled,
        model_id: modelId || null,
        model_access_set_id: modelAccessSetId || null,
        runtime,
        codex_home_profile: codexHomeProfile
      });
      setSettings(updated);
      setEnabled(updated.enabled);
      setModelId(updated.model_id ?? '');
      setModelAccessSetId(updated.model_access_set_id ?? '');
      setRuntime(updated.runtime || 'dind-docker');
      setCodexHomeProfile(updated.codex_home_profile || 'validation');
      setSuccess('UI validation settings saved.');
      setLocalError(null);
    } catch (err) {
      reportError('Failed to save UI validation settings', err, setLocalError);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const status = settings?.setup_status;

  return (
    <section className="stack qa-settings37" aria-labelledby="qa-settings37-title">
      <div className="card qa-settings37-card">
        <div className="card-header qa-settings37-header">
          <div>
            <p className="qa-settings37-kicker">Quality assurance · Global settings</p>
            <h2 id="qa-settings37-title">UI Validation</h2>
            <p>Configure the global agent model used for high-level UI validation runs.</p>
          </div>
          <Link to="/settings/models" className="link-button">
            Tool Settings
          </Link>
        </div>
        {localError && <ErrorBanner message={localError} onDismiss={() => setLocalError(null)} />}
        {success && (
          <div className="status" role="status" style={{ color: 'var(--color-success-text)' }}>
            {success}
          </div>
        )}
        {loading && (
          <p className="status" role="status">Loading validation settings...</p>
        )}
        {status && (
          <div className={`qa-settings37-readiness ${status.ready ? 'status' : 'error'}`}>
            <strong>Saved configuration</strong>
            <span>{status.message}</span>
          </div>
        )}
        <form className="stack qa-settings37-form" onSubmit={handleSave}>
          <div className="qa-settings37-enable">
            <label className="checkbox-row">
              <input
                type="checkbox"
                aria-describedby="qa-settings37-enable-help"
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
              Enable UI validation
            </label>
            <p id="qa-settings37-enable-help" className="qa-settings37-help">
              Changes take effect when saved. A ready configuration needs UI validation enabled
              and a global Codex model with global auth.
            </p>
          </div>
          <div className="qa-settings37-columns">
            <fieldset>
              <legend>01 · Model &amp; authentication</legend>
              <p id="qa-settings37-model-help" className="qa-settings37-help">
                Choose a global Codex model with a separate validation auth attached.
                Manage models and auth in Tool Settings.
              </p>
              <label>
                Validation model
                <select
                  aria-describedby="qa-settings37-model-help"
                  value={modelId}
                  onChange={(event) => setModelId(event.target.value)}
                >
                  <option value="">Select a global Codex model</option>
                  {codexModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} {model.model ? `(${model.model})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {selectedModel && (
                <dl className="qa-settings37-model-summary">
                  <dt>Selected model</dt>
                  <dd>{selectedModel.name}</dd>
                  <dt>Model ID</dt>
                  <dd className="mono">{selectedModel.model || 'codex default'}</dd>
                  <dt>Auth</dt>
                  <dd>{selectedAuth ? `${selectedAuth.name} (${selectedAuth.auth_type})` : 'Not attached'}</dd>
                  <dt>Scope</dt>
                  <dd>{selectedModel.is_global && selectedAuth?.is_global ? 'Global' : 'Incomplete'}</dd>
                </dl>
              )}
              {codexModels.length === 0 && (
                <p className="hint">
                  Create a global Codex auth and a global Codex model in Tool Settings, then return here.
                </p>
              )}
            </fieldset>
            <fieldset>
              <legend>02 · Runner configuration</legend>
              <p className="qa-settings37-help">Configure the runtime and profile used by validation runs.</p>
              <div className="qa-settings37-runtime-grid">
                <label>
                  Runtime
                  <select
                    aria-describedby="qa-settings37-runtime-help"
                    value={runtime}
                    onChange={(event) => setRuntime(event.target.value)}
                  >
                    <option value="dind-docker">DIND Docker</option>
                    <option value="local-docker">Local Docker</option>
                  </select>
                </label>
                <label>
                  Codex profile
                  <input
                    aria-describedby="qa-settings37-profile-help"
                    value={codexHomeProfile}
                    onChange={(event) => setCodexHomeProfile(event.target.value)}
                    placeholder="validation"
                  />
                </label>
              </div>
              <p id="qa-settings37-runtime-help" className="qa-settings37-help">
                Default runtime: DIND Docker. Local Docker is also supported.
              </p>
              <p id="qa-settings37-profile-help" className="qa-settings37-help">
                Codex profile is required: ASCII letters, digits, hyphens and underscores only.
                Default: <code>validation</code>.
              </p>
              <label>
                Model access set
                <input
                  aria-describedby="qa-settings37-access-help"
                  value={modelAccessSetId}
                  onChange={(event) => setModelAccessSetId(event.target.value)}
                  placeholder="validation-models"
                />
              </label>
              <p id="qa-settings37-access-help" className="qa-settings37-help">
                Optional access set identifier. The example is a placeholder, not a default.
              </p>
            </fieldset>
          </div>
          <div className="actions qa-settings37-actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save UI validation settings'}
            </button>
            <button className="secondary" type="button" onClick={load} disabled={loading || saving}>
              Refresh
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
